import { Clock3, FileText, History, KeyRound, Newspaper, RefreshCw, Search, ShieldCheck, UserRound } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { AdminSelect } from "../../components/AdminSelect";
import { useAuth } from "../../context/AuthContext";
import { useAdminToast } from "../../context/AdminToastContext";
import { adminErrorMessage } from "../../lib/adminFeedback";
import { supabase } from "../../lib/supabase";
import { AdminPageSkeleton } from "../../components/LoadingSkeleton";

type AuditEntityType = "news" | "content" | "user" | "account";

interface AuditLogRow {
  id: number;
  actor_email: string;
  actor_name: string;
  actor_role: "owner" | "admin" | "editor";
  action: string;
  entity_type: AuditEntityType;
  entity_id: string | null;
  entity_title: string;
  details: Record<string, unknown>;
  created_at: string;
}

const actionLabels: Record<string, string> = {
  "news.created": "Beitrag erstellt",
  "news.updated": "Beitrag bearbeitet",
  "news.published": "Beitrag veröffentlicht",
  "news.unpublished": "Veröffentlichung zurückgenommen",
  "news.archived": "Beitrag archiviert",
  "news.deleted": "Beitrag gelöscht",
  "news.imported": "Beitragsarchiv importiert",
  "content.updated": "Seiteninhalt aktualisiert",
  "teams.updated": "Mannschaften aktualisiert",
  "user.created": "Admin-Zugang angelegt",
  "user.locked": "Admin-Zugang gesperrt",
  "user.unlocked": "Admin-Zugang entsperrt",
  "user.password_reset": "Startpasswort neu vergeben",
  "user.deleted": "Admin-Zugang gelöscht",
  "account.password_changed": "Eigenes Passwort geändert",
};

const entityLabels: Record<AuditEntityType, string> = {
  news: "News",
  content: "Seiteninhalt",
  user: "Admin-Team",
  account: "Konto",
};

const entityFilterOptions: Array<{ value: "all" | AuditEntityType; label: string }> = [
  { value: "all", label: "Alle Bereiche" },
  { value: "news", label: "News" },
  { value: "content", label: "Seiteninhalte" },
  { value: "user", label: "Admin-Team" },
  { value: "account", label: "Konten" },
];

function dayKey(value: string): string {
  const date = new Date(value);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function dayLabel(value: string): string {
  const date = new Date(value);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (dayKey(value) === dayKey(today.toISOString())) return "Heute";
  if (dayKey(value) === dayKey(yesterday.toISOString())) return "Gestern";
  return new Intl.DateTimeFormat("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }).format(date);
}

function EntityIcon({ type }: { type: AuditEntityType }) {
  if (type === "news") return <Newspaper />;
  if (type === "content") return <FileText />;
  if (type === "account") return <KeyRound />;
  return <UserRound />;
}

export function AdminHistoryPage() {
  const { adminRole } = useAuth();
  const { showToast } = useAdminToast();
  const [logs, setLogs] = useState<AuditLogRow[]>([]);
  const [query, setQuery] = useState("");
  const [entityFilter, setEntityFilter] = useState<"all" | AuditEntityType>("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("admin_audit_logs").select("*").order("created_at", { ascending: false }).limit(300);
    if (error) showToast({ type: "error", title: "Historie nicht geladen", message: adminErrorMessage(error, "Die Änderungshistorie konnte nicht geladen werden.") });
    else setLogs((data || []) as AuditLogRow[]);
    setLoading(false);
  };

  useEffect(() => { if (adminRole === "owner") void load(); }, [adminRole]);

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return logs.filter((log) => {
      if (entityFilter !== "all" && log.entity_type !== entityFilter) return false;
      if (!normalizedQuery) return true;
      return `${log.actor_name} ${log.actor_email} ${log.entity_title} ${actionLabels[log.action] || log.action}`.toLowerCase().includes(normalizedQuery);
    });
  }, [logs, query, entityFilter]);

  const grouped = useMemo(() => {
    const groups = new Map<string, AuditLogRow[]>();
    filtered.forEach((log) => {
      const key = dayKey(log.created_at);
      groups.set(key, [...(groups.get(key) || []), log]);
    });
    return [...groups.values()];
  }, [filtered]);

  const todayCount = logs.filter((log) => dayKey(log.created_at) === dayKey(new Date().toISOString())).length;
  const actorCount = new Set(logs.map((log) => log.actor_email)).size;

  if (adminRole !== "owner") return <Navigate replace to="/admin" />;
  if (loading) return <AdminPageSkeleton variant="history" />;

  return (
    <>
      <div className="admin-page-heading history-page-heading">
        <div><span>Nur für Inhaber</span><h1>Änderungshistorie</h1><p>Nachvollziehen, wer wann welche Inhalte oder Zugänge geändert hat.</p></div>
        <button className="admin-secondary-button" onClick={() => void load()} disabled={loading}><RefreshCw /> {loading ? "Wird geladen…" : "Aktualisieren"}</button>
      </div>

      <div className="history-stats">
        <article><span><History /></span><div><small>Protokollierte Änderungen</small><strong>{logs.length}</strong></div></article>
        <article><span><Clock3 /></span><div><small>Änderungen heute</small><strong>{todayCount}</strong></div></article>
        <article><span><UserRound /></span><div><small>Beteiligte Personen</small><strong>{actorCount}</strong></div></article>
      </div>

      <section className="admin-panel history-panel">
        <div className="history-controls">
          <div className="history-owner-note"><ShieldCheck /><div><strong>Geschütztes Inhaber-Protokoll</strong><span>Diese Ansicht ist für andere Admin-Rollen weder sichtbar noch über die Datenbank abrufbar.</span></div></div>
          <div className="history-toolbar">
            <label className="admin-search history-search"><Search /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Person, Aktion oder Inhalt suchen…" /></label>
            <div className="history-filter"><span>Bereich</span><AdminSelect value={entityFilter} options={entityFilterOptions} ariaLabel="Historie nach Bereich filtern" onChange={setEntityFilter} /></div>
          </div>
        </div>

        <div className="history-timeline" aria-live="polite">
          {grouped.map((group) => (
            <section className="history-day" key={dayKey(group[0].created_at)}>
              <div className="history-day-label"><span>{dayLabel(group[0].created_at)}</span><small>{group.length} {group.length === 1 ? "Änderung" : "Änderungen"}</small></div>
              <div className="history-events">
                {group.map((log) => (
                  <article className="history-event" key={log.id}>
                    <span className={`history-event-icon history-event-icon--${log.entity_type}`}><EntityIcon type={log.entity_type} /></span>
                    <div className="history-event-copy">
                      <span className="history-event-action">{actionLabels[log.action] || log.action}</span>
                      <h2>{log.entity_title || entityLabels[log.entity_type]}</h2>
                    </div>
                    <div className="history-actor"><span>{(log.actor_name || log.actor_email).split(/[ .@]/).filter(Boolean).map((part) => part[0]).slice(0, 2).join("").toUpperCase()}</span><strong>{log.actor_name || log.actor_email}</strong></div>
                    <span className={`history-entity-badge history-entity-badge--${log.entity_type}`}>{entityLabels[log.entity_type]}</span>
                    <time className="history-event-time" dateTime={log.created_at}>{new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit" }).format(new Date(log.created_at))} Uhr</time>
                  </article>
                ))}
              </div>
            </section>
          ))}
          {filtered.length === 0 && <div className="history-empty"><History /><h2>Keine Änderungen gefunden</h2><p>Passe Suche oder Bereichsfilter an.</p></div>}
        </div>
      </section>
    </>
  );
}
